import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { CertificationUploadUrl } from '../response-models/certification-upload-url.model';
import { UploadUrlResponse } from '../response-models/upload-url-response.model';
import { UploadedFileInfo } from '../response-models/uploaded-file-info.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';

/**
 * File uploads: `/file-uploads/service-appointment` and `/file-uploads/certification` (hub `user-file-uploads-controller.ts`), plus the
 * S3 PUT and file checks shared with `DroneResourceService`.
 * The flow is:
 * 1. Request a pre-signed URL from the API
 * 2. Upload the file directly to S3 using the pre-signed URL
 * 3. Include the S3 key/URL in subsequent API calls
 *
 * Hub failures reject with an `ApiError`.
 */
@Injectable({
  providedIn: 'root'
})
export class FileUploadService extends ApiService {
  constructor() {
    super('file-uploads');
  }

  /**
   * Get a pre-signed URL for uploading a service appointment attachment
   */
  async getServiceAppointmentUploadUrl(filename: string): Promise<UploadUrlResponse> {
    const response = await firstValueFrom(this.request('POST', { path: 'service-appointment' }, toUploadUrlResponse, { filename }));
    if (!response) {
      throw new Error('Could not prepare the upload.');
    }
    return response;
  }

  /** Get a pre-signed URL for a certification document. The hub answers `{ url, fileUrl }` (fileUrl is the S3 key). */
  getCertificationUploadUrl(filename: string): Promise<CertificationUploadUrl> {
    return firstValueFrom(this.request('POST', { path: 'certification' }, toCertificationUploadUrl, { filename }));
  }

  /**
   * PUT a file to a pre-signed S3 URL. Uses `fetch`, not HttpClient, so the auth interceptor never sends the
   * Firebase token to S3. Pass `publicRead` only when the hub signed the URL with the `public-read` ACL
   * (e.g. Agremo imagery): the header must then match the signature.
   */
  async uploadToSignedUrl(url: string, file: File, options: { publicRead?: boolean } = {}): Promise<void> {
    const headers: Record<string, string> = { 'Content-Type': file.type || 'application/octet-stream' };
    if (options.publicRead) {
      headers['x-amz-acl'] = 'public-read';
    }
    const response = await fetch(url, { method: 'PUT', body: file, headers });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  }

  /**
   * Upload a file and get the S3 info for API submission
   * This is a convenience method that combines getUploadUrl + uploadToS3
   */
  async uploadServiceAppointmentFile(file: File): Promise<UploadedFileInfo> {
    // Step 1: Get pre-signed URL
    const urlInfo = await this.getServiceAppointmentUploadUrl(file.name);

    // Step 2: Upload to S3
    await this.uploadToSignedUrl(urlInfo.uploadUrl, file);

    // Step 3: Return the file info for API submission
    return {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
      fileExtension: file.name.split('.').pop() || '',
      s3Key: urlInfo.s3Key,
      s3Url: urlInfo.s3Url,
    };
  }

  /**
   * Upload multiple files for a service appointment
   */
  async uploadServiceAppointmentFiles(files: File[]): Promise<UploadedFileInfo[]> {
    const results: UploadedFileInfo[] = [];

    for (const file of files) {
      const info = await this.uploadServiceAppointmentFile(file);
      results.push(info);
    }

    return results;
  }

  /**
   * Get file extension from filename
   */
  getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Check if a file is an allowed type for service appointments
   * Allowed: images, PDFs, documents
   */
  isAllowedFileType(file: File): boolean {
    const allowedTypes = [
      // Images
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      // PDFs
      'application/pdf',
      // Documents
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
    ];

    // Must match VALID_DRONE_RESOURCE_EXTENSIONS on the Hub and the `accept`
    // attribute on any input feeding this service.
    const allowedExtensions = [
      'jpg', 'jpeg', 'png', 'gif', 'webp',
      'pdf',
      'doc', 'docx', 'xls', 'xlsx', 'txt',
    ];

    const extension = this.getFileExtension(file.name);

    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
  }

  /**
   * Get max file size in bytes (20MB)
   */
  getMaxFileSize(): number {
    return 20 * 1024 * 1024; // 20MB
  }

  /**
   * Format file size for display
   */
  formatFileSize(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }
}

function toCertificationUploadUrl(data: unknown): CertificationUploadUrl {
  const source = isRecord(data) ? data : {};
  return { uploadUrl: nullableString(source['url']) ?? '', fileKey: nullableString(source['fileUrl']) ?? '' };
}

function toUploadUrlResponse(data: unknown): UploadUrlResponse | null {
  const source = isRecord(data) ? data : {};
  const uploadUrl = nullableString(source['uploadUrl']);
  const s3Key = nullableString(source['s3Key']);
  if (!uploadUrl || !s3Key) {
    return null;
  }
  return {
    uploadUrl,
    s3Key,
    s3Url: nullableString(source['s3Url']) ?? '',
    fileName: nullableString(source['fileName']) ?? '',
  };
}
